import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Search, Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';

interface CasesFiltersProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  courtLevelFilter: string;
  setCourtLevelFilter: (level: string) => void;
  clientFilter: string;
  setClientFilter: (client: string) => void;
  clients: Array<{ id: string; name: string; email: string }>;
}

const statusOptions = ['Em andamento', 'Aguardando documentos', 'Concluído', 'Suspenso'];

const courtLevelOptions = [
  { value: 'all', label: 'Todas as instâncias' },
  { value: '1_instancia', label: '1ª Instância' },
  { value: '2_instancia', label: '2ª Instância' },
  { value: 'superior', label: 'Tribunais Superiores' },
  { value: 'supremo', label: 'Supremo Tribunal Federal' },
];

const triggerClass =
  'h-10 w-full min-w-0 rounded-xl border-slate-200 bg-white text-sm text-slate-700 shadow-none hover:border-slate-300 focus:ring-[#c4ab83]/30 sm:min-w-[10rem] lg:w-[11rem]';

export const CasesFilters = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  courtLevelFilter,
  setCourtLevelFilter,
  clientFilter,
  setClientFilter,
  clients,
}: CasesFiltersProps) => {
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);

  const selectedClient = clients.find((client) => client.id === clientFilter);

  return (
    <div className="flex w-full min-w-0 flex-col gap-2 lg:flex-row lg:items-center">
      {/* Busca — uma linha */}
      <div className="relative w-full lg:flex-1">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Buscar por título, número ou cliente..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="h-10 rounded-xl border-slate-200 bg-white pl-10 text-sm shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30"
        />
      </div>

      {/* Filtros — mesma linha a partir de sm */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:flex">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className={triggerClass}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-50 border border-border bg-background shadow-lg">
            <SelectItem value="all">Todos os status</SelectItem>
            {statusOptions.map((status) => (
              <SelectItem key={status} value={status}>
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={courtLevelFilter} onValueChange={setCourtLevelFilter}>
          <SelectTrigger className={triggerClass}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-50 border border-border bg-background shadow-lg">
            {courtLevelOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Popover open={clientPopoverOpen} onOpenChange={setClientPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={clientPopoverOpen}
              className={`${triggerClass} justify-between font-normal`}
            >
              <span className="truncate">
                {selectedClient ? selectedClient.name : 'Todos os clientes'}
              </span>
              <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[min(100vw-2rem,18rem)] p-0" align="start">
            <Command>
              <CommandInput placeholder="Buscar cliente..." className="h-9 text-sm" />
              <CommandList>
                <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="all"
                    onSelect={() => {
                      setClientFilter('all');
                      setClientPopoverOpen(false);
                    }}
                  >
                    <Check className={`mr-2 h-4 w-4 ${clientFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                    Todos os clientes
                  </CommandItem>
                  {clients.map((client) => (
                    <CommandItem
                      key={client.id}
                      value={client.name}
                      onSelect={() => {
                        setClientFilter(client.id);
                        setClientPopoverOpen(false);
                      }}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${clientFilter === client.id ? 'opacity-100' : 'opacity-0'}`}
                      />
                      {client.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
